import { Close as CloseIcon } from '@mui/icons-material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box, Button, IconButton, Modal, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Observable, Subscription } from 'rxjs';
import { Fight } from '../../../../../../corelogic/domain/models/fight.model';
import { Fighter } from '../../../../../../corelogic/domain/models/fighter.model';
import { retrieveMostRecentFightResults } from '../../../../../../corelogic/domain/usecases/fights/retrieveMostRecentFightResults.usecase';
import { getFights } from '../../../../../../corelogic/state/fight/fight.selector';
import { store } from '../../../../../../corelogic/state/store';
import SelectorModalCard from '../SelectorModalCard/SelectorModalCard';

interface SelectorModalProps {
  open: boolean;
  onClose: () => void;
  fighterInfo: Fighter | null;
  selectedFighterInfo: Fighter | null;
  onSwapFighters: () => void;
}

const SelectorModal: React.FC<SelectorModalProps> = ({
  open,
  onClose,
  fighterInfo,
  selectedFighterInfo,
  onSwapFighters,
}) => {
  const fights$: Observable<Fight[]> = store.select(getFights);
  const [allFights, setAllFights] = useState<Fight[]>([]);

  useEffect(() => {
    const subscription: Subscription = fights$.subscribe((fights) => {
      setAllFights(fights);
    });
    return () => subscription.unsubscribe();
  }, [fights$]);

  // Add keyboard event handler
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && open && fighterInfo) {
        event.preventDefault();
        onSwapFighters();
        onClose();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [open, fighterInfo, onSwapFighters, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="selector-modal"
      aria-describedby="select-swap-fighters"
    >
      <Box
        sx={{
          position: 'absolute',
          top: { xs: 0, sm: '50%' },
          left: { xs: 0, sm: '50%' },
          transform: { xs: 'none', sm: 'translate(-50%, -50%)' },
          width: { xs: '100%', sm: selectedFighterInfo ? '50vw' : '25vw' },
          height: { xs: 'calc(100vh - 56px)', sm: 'auto' },
          maxHeight: { xs: '100%', sm: '80vh' },
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          border: { xs: 'none', sm: '1px solid rgba(255, 255, 255, 0.1)' },
          boxShadow: { xs: 'none', sm: 24 },
          borderRadius: { xs: 0, sm: '16px' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: '8px',
            top: '8px',
            color: 'white',
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: { xs: '12px', sm: '15px' },
            bgcolor: 'rgba(139, 0, 0, 0.5)',
            borderRadius: { xs: 0, sm: '16px 16px 0 0' },
            marginBottom: { xs: '12px', sm: '20px' },
          }}
        >
          {selectedFighterInfo ? (
            <>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: 'Rubik Mono One',
                  flex: 1,
                  textAlign: 'center',
                  fontSize: {
                    xs: '0.7rem',
                    sm: 'clamp(0.4rem, 1.2vw, 1.5rem)',
                  },
                  color: 'white',
                }}
              >
                New Fighter
              </Typography>
              <Box
                sx={{
                  flex: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0 8px',
                }}
              >
                <SwapHorizIcon
                  sx={{
                    fontSize: {
                      xs: '0.9rem',
                      sm: 'clamp(0.4rem, 1.2vw, 1.5rem)',
                    },
                    color: 'white',
                  }}
                />
              </Box>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: 'Rubik Mono One',
                  flex: 1,
                  textAlign: 'center',
                  fontSize: {
                    xs: '0.7rem',
                    sm: 'clamp(0.4rem, 1.2vw, 1.5rem)',
                  },
                  color: 'white',
                }}
              >
                Current Fighter
              </Typography>
            </>
          ) : (
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Rubik Mono One',
                flex: 1,
                textAlign: 'center',
                fontSize: { xs: '0.7rem', sm: 'clamp(0.4rem, 1.2vw, 1.5rem)' },
                color: 'white',
              }}
            >
              New Fighter
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flex: 1,
            overflowY: 'auto',
            maxHeight: 'calc(100% - 100px)',
            gap: { xs: 1, sm: 2 },
            px: { xs: 1, sm: 2 },
          }}
        >
          {selectedFighterInfo ? (
            // Two-card layout when selectedFighterInfo exists
            <>
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minWidth: 0,
                }}
              >
                {fighterInfo && (
                  <SelectorModalCard
                    fighter={fighterInfo}
                    color="rgba(50, 50, 50, 0.8)"
                    mostRecentFightResults={retrieveMostRecentFightResults(
                      allFights,
                      fighterInfo.id,
                      5,
                    )}
                  />
                )}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minWidth: 0,
                }}
              >
                <SelectorModalCard
                  fighter={selectedFighterInfo}
                  color="rgba(50, 50, 50, 0.8)"
                  mostRecentFightResults={retrieveMostRecentFightResults(
                    allFights,
                    selectedFighterInfo.id,
                    5,
                  )}
                />
              </Box>
            </>
          ) : (
            // Single-card layout when selectedFighterInfo is null
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              {fighterInfo && (
                <SelectorModalCard
                  fighter={fighterInfo}
                  color="rgba(50, 50, 50, 0.8)"
                  mostRecentFightResults={retrieveMostRecentFightResults(
                    allFights,
                    fighterInfo.id,
                    5,
                  )}
                />
              )}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: { xs: '8px', sm: '12px' },
          }}
        >
          <Button
            variant="contained"
            sx={{
              fontSize: { xs: '0.9rem', sm: 'clamp(0.5rem, 0.8vw, 1rem)' },
              fontWeight: 'bold',
              bgcolor: 'rgba(50, 50, 50, 0.8)',
              color: 'white',
              textTransform: 'none',
              py: { xs: 1, sm: 1.5 },
              px: { xs: 3, sm: 4 },
              '&:hover': {
                bgcolor: 'rgba(139, 0, 0, 0.8)',
              },
            }}
            onClick={onSwapFighters}
          >
            {selectedFighterInfo ? 'Swap Fighters' : 'Confirm Fighter'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SelectorModal;
